import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Link2Off, Link2, CheckCircle2, XCircle, Settings2, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function PmcPayLookup({ initialPlate = "" }) {
  const [status, setStatus] = useState(null);
  const [plate, setPlate] = useState(initialPlate);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [auditing, setAuditing] = useState(false);

  // connect flow
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  // manage lots
  const [manage, setManage] = useState(false);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);

  const loadStatus = async () => {
    try { setStatus((await api.get("/pmcpay/status")).data); }
    catch { setStatus({ connected: false, can_manage: false }); }
  };
  useEffect(() => { loadStatus(); }, []);

  const sendCode = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post("/pmcpay/connect/start", { email: email.trim() }); setCodeSent(true); toast.success(`Code sent to ${email.trim()}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send code"); }
    finally { setBusy(false); }
  };
  const verifyCode = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pmcpay/connect/verify", { email: email.trim(), otp: otp.trim() });
      toast.success(`Connected as ${r.data.user_name || email.trim()}`);
      setCodeSent(false); setOtp(""); await loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid or expired code"); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect PMC Pay?")) return;
    await api.post("/pmcpay/disconnect"); await loadStatus(); toast.success("Disconnected");
  };

  const selectLot = async (locationId) => {
    setStatus((s) => ({ ...s, selected_lot: locationId }));
    setResult(null);
    try { await api.post("/pmcpay/select-lot", { locationId }); } catch { /* ignore */ }
  };

  const searchLots = async () => {
    setBusy(true);
    try { setFound((await api.get(`/pmcpay/lots?query=${encodeURIComponent(q.trim())}`)).data.lots || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };
  const addLot = async (lot) => {
    const lots = [...(status.lots || [])];
    if (lots.some((l) => l.locationId === lot.locationId)) { toast.info("Already added"); return; }
    lots.push(lot);
    const r = await api.post("/pmcpay/lots/save", { lots });
    setStatus((s) => ({ ...s, lots: r.data.lots, selected_lot: s.selected_lot || lot.locationId }));
    toast.success(`Added ${lot.name}`);
  };
  const removeLot = async (locationId) => {
    const lots = (status.lots || []).filter((l) => l.locationId !== locationId);
    const r = await api.post("/pmcpay/lots/save", { lots });
    setStatus((s) => ({ ...s, lots: r.data.lots }));
  };

  const runAudit = async () => {
    setAuditing(true);
    try { const r = await api.post("/pmcpay/audit"); toast.success(`Audit done — flagged ${r.data.flagged_count} booted car(s) that had paid`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Audit failed"); }
    finally { setAuditing(false); }
  };

  const search = async (e) => {
    e?.preventDefault();
    if (plate.trim().length < 1) return;
    setBusy(true);
    try { setResult((await api.get(`/pmcpay/lookup?plate=${encodeURIComponent(plate.trim())}`)).data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Lookup failed"); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <Card className="bg-[#131B2A] border-[#28364F] p-4 space-y-3" data-testid="pmcpay-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="font-display font-bold flex items-center gap-2"><Search className="w-4 h-4 text-cyan-400" /> PMC Pay plate lookup</div>
          <p className="text-xs text-[#94A3B8]">Check if a plate has a paid parking session before you tag or boot it.</p>
        </div>
        {status.connected ? (
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-1" data-testid="pmcpay-connected">
            <Link2 className="w-3 h-3" /> {status.user_name ? status.user_name.split(" - ")[0] : "Connected"}
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-amber-500/40 text-amber-300 inline-flex items-center gap-1" data-testid="pmcpay-not-connected"><Link2Off className="w-3 h-3" /> Not connected</span>
        )}
      </div>

      {/* CONNECT (admins only) */}
      {!status.connected && (
        status.can_manage ? (
          <div className="rounded-md border border-[#28364F] bg-[#0B0F17] p-3 space-y-2" data-testid="pmcpay-connect">
            <div className="text-xs text-[#94A3B8]">Sign in with your PMC Pay email — they'll text/email you a one-time code.</div>
            <div className="flex gap-2">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@pmcpay-email.com" type="email"
                className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="pmcpay-email-input" />
              <Button onClick={sendCode} disabled={busy || !email.trim()} className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold shrink-0" data-testid="pmcpay-send-code-btn">Send code</Button>
            </div>
            {codeSent && (
              <div className="flex gap-2">
                <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" inputMode="numeric"
                  className="bg-[#0B0F17] border-[#28364F] text-white tracking-widest" data-testid="pmcpay-otp-input" />
                <Button onClick={verifyCode} disabled={busy || !otp.trim()} className="bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold shrink-0" data-testid="pmcpay-verify-btn">Connect</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-amber-300" data-testid="pmcpay-ask-admin">PMC Pay isn't connected yet — ask an admin to connect it.</div>
        )
      )}

      {/* CONNECTED: lot selector + lookup */}
      {status.connected && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-[#94A3B8]">Lot:</label>
            <select value={status.selected_lot || ""} onChange={(e) => selectLot(e.target.value)}
              className="flex-1 min-w-[180px] bg-[#0B0F17] border border-[#28364F] rounded-md text-white text-sm px-2 py-2" data-testid="pmcpay-lot-select">
              {(status.lots || []).length === 0 && <option value="">No lots saved yet</option>}
              {(status.lots || []).map((l) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
            </select>
            {status.can_manage && (
              <Button variant="outline" onClick={() => setManage((m) => !m)} className="shrink-0 bg-[#0B0F17] border-[#28364F] text-cyan-300" data-testid="pmcpay-manage-btn"><Settings2 className="w-4 h-4" /></Button>
            )}
          </div>

          {status.can_manage && (
            <Button onClick={runAudit} disabled={auditing} variant="outline" className="w-full bg-[#0B0F17] border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="pmcpay-audit-btn">
              {auditing ? "Auditing booted cars…" : "Audit booted cars vs PMC paid list"}
            </Button>
          )}

          {manage && status.can_manage && (
            <div className="rounded-md border border-[#28364F] bg-[#0B0F17] p-3 space-y-2" data-testid="pmcpay-manage-panel">
              <div className="flex items-center justify-between">
                <div className="text-xs text-[#94A3B8]">Your saved lots</div>
                <Button variant="ghost" size="sm" className="text-red-400 h-6 px-2" onClick={disconnect} data-testid="pmcpay-disconnect-btn">Disconnect</Button>
              </div>
              <div className="space-y-1">
                {(status.lots || []).map((l) => (
                  <div key={l.locationId} className="flex items-center justify-between text-xs bg-[#131B2A] rounded px-2 py-1.5 border border-[#28364F]">
                    <span>{l.name}</span>
                    <button onClick={() => removeLot(l.locationId)} className="text-red-400" data-testid={`pmcpay-remove-${l.locationId}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lots by address…"
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="pmcpay-lot-search-input" />
                <Button onClick={searchLots} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold shrink-0" data-testid="pmcpay-lot-search-btn">Find</Button>
              </div>
              {found.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="pmcpay-lot-results">
                  {found.map((l) => (
                    <div key={l.locationId} className="flex items-center justify-between text-xs bg-[#131B2A] rounded px-2 py-1.5 border border-[#28364F]">
                      <span>{l.name}</span>
                      <button onClick={() => addLot(l)} className="text-emerald-400 inline-flex items-center gap-1" data-testid={`pmcpay-add-${l.locationId}`}><Plus className="w-3.5 h-3.5" /> Add</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <form onSubmit={search} className="flex gap-2">
            <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} placeholder="ABC-1234"
              className="bg-[#0B0F17] border-[#28364F] text-white plate uppercase tracking-widest" data-testid="pmcpay-plate-input" />
            <Button type="submit" disabled={busy || plate.trim().length < 1 || !status.selected_lot} className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold shrink-0" data-testid="pmcpay-search-btn">
              {busy ? "Searching…" : "Search"}
            </Button>
          </form>

          {result && result.connected && (
            <div className="rounded-md border border-[#28364F] bg-[#0B0F17] p-3" data-testid="pmcpay-result">
              {result.matches && result.matches.length > 0 ? (
                <div className="space-y-1.5">
                  <div className="text-emerald-300 font-bold flex items-center gap-2" data-testid="pmcpay-paid"><CheckCircle2 className="w-4 h-4" /> {result.plate} has an active record — DO NOT BOOT</div>
                  {result.matches.map((m, i) => (
                    <div key={`${m.tag}-${m.state}-${m.type}-${m.status}`} className="text-xs text-white font-mono">• {m.tag} ({m.state}) — <span className="uppercase text-cyan-300">{m.type}</span> · {m.status}</div>
                  ))}
                  <div className="text-[11px] text-[#64748B]">Lot: {result.lot_name || result.locationId}</div>
                </div>
              ) : (
                <div className="text-amber-300 flex items-center gap-2" data-testid="pmcpay-notpaid"><XCircle className="w-4 h-4" /> No active paid session for {result.plate} in {result.lot_name || "this lot"}.</div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
